import React, { memo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Scissors,
  Home, LayoutDashboard, Package, LogOut, Upload, Book, AlertOctagon,
  ClipboardList, ClipboardCheck, FileText, Calendar, Shield, ChevronDown, ChevronRight, ScrollText, Warehouse, Truck, Ship, Tent,
  Store, TrendingUp, Award, Construction, Users, RotateCcw, Lasso, Container, UserCog, X, Globe, Lightbulb, MapPin, Footprints, Clock, Map, Factory,
  ShoppingBag, BarChart3, Layers, Zap, Target, Radar, BadgeDollarSign, CalendarClock, PackageOpen, Calculator
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { B } from '../utils/constants';
import { useRole } from '../context/RoleContext';

// Supply Round Up items (order per Nate 2026-07-31; Pipeline folded in from
// the retired "On the Trail" section)
const roundUpItems = [
  { id: 'buy-ready', label: 'Buy Ready', icon: Clock, path: '/supply-chain/buy-ready' },
  { id: 'launch-lineup', label: 'Launch Line Up', icon: Calendar, path: '/supply-chain/launch-lineup' },
  { id: 'pipeline', label: 'Pipeline', icon: Ship, path: '/supply-chain/pipeline' },
  { id: 'grit-grind', label: 'Grit and Grind', icon: Package, path: '/supply-chain/grit-grind' },
  { id: 'supply-plan', label: 'Supply Plan', icon: TrendingUp, path: '/supply-chain/supply-plan' },
  // Directly under Supply Plan (Alexis 2026-09-01): same subject — POs placed
  // and received — with the WFX receipt plan laid against them.
  { id: 'projection-vs-po', label: 'Projection vs POs', icon: Target, path: '/supply-chain/projection-vs-po', beta: true },
  { id: 'custom-boots', label: 'Custom Boots', icon: Footprints, path: '/supply-chain/custom-boots' },
  { id: 'embroidery', label: 'Embroidery', icon: Scissors, path: '/supply-chain/embroidery' },
  { id: 'trail-notes', label: 'Trail Notes', icon: FileText, path: '/supply-chain/trail-notes' },
  { id: 'mx-launch-tracker', label: 'Mexico Production Tracker', icon: Factory, path: '/supply-chain/mx-launch-tracker', beta: true },
  // The Ledger — accounting-facing views grouped together (2026-08-17): the Cash Flow
  // statement (weekly payment runs) and the PO Price Audit. Parent shows when the
  // user can access at least one child; each child is gated by its own resource key.
  {
    id: 'the-ledger',
    label: 'The Ledger',
    icon: BadgeDollarSign,
    path: '/supply-chain/cash-flow',
    children: [
      { id: 'cash-flow', label: 'Cash Flow', path: '/supply-chain/cash-flow' },
      { id: 'po-price-audit', label: 'PO Price Audit', path: '/supply-chain/po-price-audit' },
    ],
  },
];

// Carrier sub-items (under Carrier Management). Per-carrier dashboards
// for Expeditors, Kuehne+Nagel, and EFW were retired in v1.44.x — their
// configuration and API tests live on /admin/carriers now. Cargo Signal
// stays because it's a different real-time GPS tool, not just a tracker.
const carrierSubItems = [
  { id: 'cargo-signal', label: 'Cargo Signal', icon: MapPin, path: '/supply-chain/cargo-signal' },
];

// Data Corral sub-items — Wild West and Outpost are exception/audit views
// that operate on the same imported data, so they group naturally under
// the Data Corral parent.
const dataCorralSubItems = [
  { id: 'wild-west', label: 'Wild West', icon: AlertOctagon, path: '/wild-west' },
  { id: 'outpost',   label: 'Outpost',   icon: Tent,         path: '/outpost'   },
];

// The Haystack — meta / housekeeping group: backlog of asks, feature
// request inbox, and what shipped per release.
const haystackSubItems = [
  { id: 'backlog',                  label: 'Backlog',          icon: ClipboardList, path: '/backlog' },
  { id: 'admin-feature-requests',   label: 'Feature Requests', icon: Lightbulb,     path: '/admin/feature-requests', adminOnly: true },
  { id: 'changelog',                label: 'Changelog',        icon: ScrollText,    path: '/changelog' },
];

// Ranchhands — admin people-management group: roles + users in one place.
const ranchhandsSubItems = [
  { id: 'admin-roles', label: 'Role Management', icon: Shield,  path: '/admin/roles', adminOnly: true },
  { id: 'admin-users', label: 'User Management', icon: UserCog, path: '/admin/users', adminOnly: true },
];

// The Barn (Inventory) items
const barnItems = [
  { id: 'the-barn', label: 'Inventory', icon: Container, path: '/supply-chain/the-barn' },
  { id: 'trail-count', label: 'Trail Count', icon: Radar, path: '/supply-chain/trail-count' },
  { id: 'store-inbound', label: 'Store Inbound', icon: PackageOpen, path: '/supply-chain/store-inbound' },
];

// Trading Post (Analytics) items
//
// The QBR nav carries the ACTIVE QUARTER ONLY. Retired quarters keep their
// frozen files and routes so old links and exported decks still resolve, but
// they are no longer listed here — the quarterly record now lives as exported
// presentations in Drive rather than as an accumulating stack of nav entries.
// Rolling to the next quarter is a one-line change to this block.
//
// Wholesale is a React route rather than a hash into the QBR iframe, which is
// why it carries `path` instead of `hash`; it sits directly after Ops &
// Logistics (2026-07-23 request), matching the exec-summary scorecard order.
const tradingPostItems = [
  { id: 'qbr', label: 'QBR', icon: TrendingUp, path: '/supply-chain/qbr',
    children: [
      { id: 'qbr-q3', label: '2026 Q3', basePath: '/supply-chain/qbr', children: [
        { id: 'qbr-q3-exec', label: 'Exec Summary', hash: 'exec' },
        { id: 'qbr-q3-ops',  label: 'Ops & Logistics', hash: 'ops' },
        { id: 'qbr-wholesale', label: 'Wholesale', path: '/supply-chain/qbr/wholesale', tooltip: 'Wholesale QBR — units, plan attainment, segment mix' },
        { id: 'qbr-q3-plan', label: 'Planning + Allocation', hash: 'plan', underConstruction: true, tooltip: 'Demand planning & allocation reporting — coming soon' },
        { id: 'qbr-q3-src',  label: 'Sourcing', hash: 'src' },
        { id: 'qbr-q3-pd',   label: 'Product Development', hash: 'pd' },
        { id: 'qbr-q3-qc',   label: 'Quality Control', hash: 'qc' },
      ]},
    ]},
];

// The Mercantile (WFX-sourced product & merch data) items.
// PLM Dashboards reads from wfx_costing for margin/assortment reporting;
// Material Planning reads from wfx_items + wfx_bom for material demand.
// Both refresh nightly via the scheduled-sync orchestrator.
const mercantileItems = [
  {
    id: 'plm-dashboards',
    label: 'Margin Reporting',
    icon: BarChart3,
    path: '/workshop/margin-reporting',
    children: [
      { id: 'plm-season-reporting',   label: 'Per Season',            path: '/workshop/margin-reporting/season-reporting' },
      { id: 'plm-season-over-season', label: 'Historical Comparison', path: '/workshop/margin-reporting/season-over-season' },
    ],
  },
  {
    id: 'material-planning',
    label: 'Material Planning',
    icon: Layers,
    path: '/workshop/material-planning',
    // Leather keeps the original URL (bookmarks, links); Apparel and
    // Accessories are sibling pages behind the same 'material-planning'
    // resource key — whoever can see one can see the others.
    children: [
      { id: 'material-planning-leather', label: 'Leather', path: '/workshop/material-planning' },
      // Label says "Apparel Fabric" (the page plans fabric only) while the URL stays /apparel — deliberate, don't "fix" the path.
      { id: 'material-planning-apparel', label: 'Apparel Fabric', path: '/workshop/material-planning/apparel' },
      // Accessories leather. Separate tab on purpose: accessory BOMs are still
      // being rebuilt, so its numbers must never mix into the Leather page.
      { id: 'material-planning-accessories', label: 'Accessory Leather & Hardware', path: '/workshop/material-planning/accessories', beta: true },
    ],
  },
  {
    // PD's pre-costing estimator — seed cost/margin from WFX before an item
    // (or its cost sheet) exists, swap components, override anything.
    // Assumptions is PD-managed config behind its own resource key
    // ('margin-builder-assumptions') so it can be granted to just Taylor +
    // admins; the link renders for anyone with Margin Builder access but the
    // route itself stays gated.
    id: 'margin-builder',
    label: 'Margin Builder',
    icon: Calculator,
    beta: true,
    path: '/workshop/margin-builder',
    children: [
      { id: 'margin-builder-estimator', label: 'Estimator', path: '/workshop/margin-builder' },
      { id: 'margin-builder-assumptions', label: 'Assumptions', path: '/workshop/margin-builder/assumptions' },
    ],
  },
  {
    // Planning's projection push — paste the buy plan, write DTC/WS unit
    // projections back to WFX. Lives in The Workshop because that's where the
    // WFX-sourced pages are; the URL is /planning/wfx-import because it's
    // Planning's tool.
    id: 'wfx-projection-import',
    label: 'WFX Projection Import',
    icon: Upload,
    path: '/planning/wfx-import',
  },
];

// Vendors items
const vendorsItems = [
  { id: 'vendors-lineup', label: 'Line Up', icon: LayoutDashboard, path: '/vendors/line-up' },
  { id: 'vendors-scorecard', label: 'Scorecard', icon: Award, path: '/vendors/scorecard', underConstruction: true },
  { id: 'vendors-tanneries', label: 'Tanneries', icon: Factory, path: '/vendors/tanneries' },
  { id: 'vendor-actions', label: 'Vendor Actions', icon: ClipboardList, path: '/vendors/vendor-actions' },
  { id: 'vendor-approvals', label: 'Approvals', icon: ClipboardCheck, path: '/vendors/approvals' },
];

// Howdy Partner (Customer) items
const howdyPartnerItems = [
  { id: 'customer-journey', label: 'Customer Journey', icon: Map, path: '/customer/journey' },
  { id: 'customer-journey-operations', label: 'Operator Dashboard', icon: Zap, path: '/customer-journey/operations' },
  { id: 'boots-on-the-move', label: 'Boots on the Move', icon: Footprints, path: '/customer/boots-on-the-move' },
  { id: 'order-recon', label: 'Order Recon', icon: ClipboardCheck, path: '/customer/order-recon' },
  { id: 'store-order-schedule', label: 'Store Schedule', icon: CalendarClock, path: '/customer/store-order-schedule' },
];

// The Exchange (Returns) items
const exchangeItems = [
  { id: 'the-exchange', label: 'Return Analytics', icon: TrendingUp, path: '/supply-chain/the-exchange' },
  { id: 'quality-concerns', label: 'Quality Concerns', icon: AlertOctagon, path: '/supply-chain/quality-concerns' },
];

// Wholesale microsite items. The group header links to the /wholesale landing
// page; ZM10 is the first live report. Distinct from "The Mercantile" (PLM) —
// this is the wholesale team's own reporting home.
const wholesaleItems = [
  { id: 'wholesale',           label: 'Overview',      icon: LayoutDashboard, path: '/wholesale' },
  { id: 'wholesale-zm10',      label: 'ZM10 Pipeline', icon: Package,        path: '/wholesale/zm10' },
  { id: 'wholesale-fill-rate', label: 'Trail Boss',    icon: Target,         path: '/wholesale/fill-rate' },
  { id: 'wholesale-price-check', label: 'Price Check', icon: BadgeDollarSign, path: '/wholesale/price-check' },
];

// Sheriff's Office (Admin) items. Wild West/Outpost group under Data
// Corral; Backlog/Feature Requests/Changelog group under The Haystack;
// Role + User Management group under Ranchhands. Carrier Management
// keeps Cargo Signal as its only sub-item (Expeditors/KN/EFW retired).
const sheriffItems = [
  { id: 'data-corral',     label: 'Data Corral',         icon: Upload, path: '/data-corral' },
  { id: 'process-map',     label: 'Process Map',         icon: Map,    path: '/process-map' },
  { id: 'haystack',        label: 'The Haystack',        icon: Layers, path: null /* collapsible parent, no own page */ },
  { id: 'admin-carriers',  label: 'Horse Stable',        icon: Truck,  path: '/admin/carriers', adminOnly: true },
  { id: 'ranchhands',      label: 'Ranchhands',          icon: Users,  path: null /* collapsible parent, no own page */, adminOnly: true },
];

function Sidebar({ activePage, onNavigate, user, onLogout, onClose, isMobile }) {
  const location = useLocation();
  const { hasAccess, isAdmin, loading: roleLoading } = useRole();
  const { t, i18n } = useTranslation();

  // Language toggle handler - handle language variants like en-US
  const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  // Round Up paths
  const roundUpPaths = roundUpItems.flatMap(item => [item.path, ...(item.children || []).map(c => c.path)]);
  const isOnRoundUpPage = roundUpPaths.some(path => location.pathname.startsWith(path));



  // The Barn paths
  const barnPaths = barnItems.map(item => item.path);
  const isOnBarnPage = barnPaths.some(path => location.pathname.startsWith(path));

  // Vendors paths
  const vendorsPaths = vendorsItems.map(item => item.path);
  const isOnVendorsPage = vendorsPaths.some(path => location.pathname.startsWith(path));

  // Trading Post paths (includes nested Vendors paths)
  const tradingPostPaths = [...tradingPostItems.map(item => item.path), ...vendorsPaths];
  const isOnTradingPostPage = tradingPostPaths.some(path => location.pathname.startsWith(path));

  // The Mercantile paths
  const mercantilePaths = mercantileItems.map(item => item.path);
  const isOnMercantilePage = mercantilePaths.some(path => location.pathname.startsWith(path));

  // Howdy Partner paths
  const howdyPartnerPaths = howdyPartnerItems.map(item => item.path);
  const isOnHowdyPartnerPage = howdyPartnerPaths.some(path => location.pathname.startsWith(path));

  // The Exchange paths
  const exchangePaths = exchangeItems.map(item => item.path);
  const isOnExchangePage = exchangePaths.some(path => location.pathname.startsWith(path));

  // Wholesale paths
  const wholesalePaths = wholesaleItems.map(item => item.path);
  const isOnWholesalePage = location.pathname.startsWith('/wholesale');

  // Carrier sub-items paths
  const carrierSubPaths = carrierSubItems.map(item => item.path);
  const isOnCarrierSubPage = carrierSubPaths.some(path => location.pathname.startsWith(path));

  // Data Corral / Haystack / Ranchhands sub-items paths
  const dataCorralSubPaths = dataCorralSubItems.map(item => item.path);
  const isOnDataCorralSubPage = dataCorralSubPaths.some(path => location.pathname.startsWith(path));
  const haystackSubPaths = haystackSubItems.map(item => item.path);
  const isOnHaystackSubPage = haystackSubPaths.some(path => location.pathname.startsWith(path));
  const ranchhandsSubPaths = ranchhandsSubItems.map(item => item.path);
  const isOnRanchhandsSubPage = ranchhandsSubPaths.some(path => location.pathname.startsWith(path));

  // Sheriff's Office paths (including admin routes and all sub-group pages)
  const sheriffPaths = [
    ...sheriffItems.filter(i => i.path).map(item => item.path),
    '/admin',
    ...carrierSubPaths,
    ...dataCorralSubPaths,
    ...haystackSubPaths,
    ...ranchhandsSubPaths,
  ];
  const isOnSheriffPage = sheriffPaths.some(path => location.pathname.startsWith(path));

  // Default to collapsed, but start expanded if user is on that page
  const [expanded, setExpanded] = useState({
    roundUp: isOnRoundUpPage,

    barn: isOnBarnPage,
    tradingPost: isOnTradingPostPage,
    qbr: location.pathname.startsWith('/supply-chain/qbr'),
    'qbr-q3': true,                                      // the only quarter listed — always open
    vendors: isOnVendorsPage,
    mercantile: isOnMercantilePage,
    plmDashboardsSub: location.pathname.startsWith('/workshop/margin-reporting'),
    materialplanningSub: location.pathname.startsWith('/workshop/material-planning'),
    marginbuilderSub: location.pathname.startsWith('/workshop/margin-builder'),
    theledgerSub: location.pathname.startsWith('/supply-chain/cash-flow') || location.pathname.startsWith('/supply-chain/po-price-audit'),
    howdyPartner: isOnHowdyPartnerPage,
    exchange: isOnExchangePage,
    wholesale: isOnWholesalePage,
    sheriff: isOnSheriffPage,
    carrierSub: isOnCarrierSubPage,
    dataCorralSub: isOnDataCorralSubPage,
    haystack: isOnHaystackSubPage,
    ranchhands: isOnRanchhandsSubPage,
  });
  const toggleSection = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  
  // Determine active state from URL
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="w-56 h-full p-5 flex flex-col" style={{ backgroundColor: B.forest }}>
      {/* Logo with Close Button for Mobile/Tablet */}
      <div className="mb-6 flex items-center justify-between">
        <Link to="/" className="block" onClick={onClose}>
          <h1 className="text-lg font-semibold tracking-widest text-white" style={{ fontFamily: 'Gill Sans, sans-serif' }}>
            {t('nav.tecovas')}
          </h1>
          <p className="text-xs" style={{ color: B.sage }}>{t('nav.supplyChain')}</p>
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="xl:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1">
        {/* The Homestead */}
        <Link
          to="/"
          onClick={onClose}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isActive('/') && location.pathname === '/' ? B.olive : 'transparent',
            color: isActive('/') && location.pathname === '/' ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif',
            fontWeight: '600'
          }}
        >
          <Home className="w-4 h-4" />
          <span>{t('nav.homestead')}</span>
        </Link>

        <div className="h-px my-2 mx-2" style={{ backgroundColor: B.olive }} />

        {/* Supply Round Up - Collapsible */}
        <button
          onClick={() => toggleSection('roundUp')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnRoundUpPage ? B.olive : 'transparent',
            color: isOnRoundUpPage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Lasso className="w-4 h-4" />
            <span>{t('nav.supplyRoundUp')}</span>
          </div>
          {expanded.roundUp ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.roundUp && (
          <div className="ml-3">
            {roundUpItems.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              // A grouped item (The Ledger) is visible when the user can reach at
              // least one child; each child link is gated by its own resource key.
              const canAccess = hasChildren
                ? item.children.some((c) => hasAccess('page', c.id))
                : hasAccess('page', item.id);
              const active = hasChildren
                ? item.children.some((c) => location.pathname.startsWith(c.path))
                : isActive(item.path);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  </div>
                );
              }

              if (hasChildren) {
                const subKey = `${item.id.replace(/-/g, '')}Sub`; // e.g. 'theledgerSub'
                const subOpen = expanded[subKey];
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => toggleSection(subKey)}
                      className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: active ? B.olive + 'cc' : 'transparent',
                        color: active ? '#fff' : B.sand,
                        fontFamily: 'Gill Sans, sans-serif',
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                      </div>
                      {subOpen
                        ? <ChevronDown className="w-3 h-3 opacity-60" />
                        : <ChevronRight className="w-3 h-3 opacity-60" />}
                    </button>
                    {subOpen && (
                      <div className="ml-4 mb-1">
                        {item.children.map((child) => {
                          const childCan = hasAccess('page', child.id);
                          const childActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                          if (!childCan) {
                            return (
                              <div key={child.id} className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                                style={{ color: B.stone, opacity: 0.5, fontFamily: 'Gill Sans, sans-serif' }}>
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: B.stone + '80' }} />
                                <span>{child.label}</span>
                              {child.beta && (
                                <span className="text-[8px] font-bold px-1 py-px rounded uppercase tracking-wider" style={{ backgroundColor: '#B87333', color: '#fff' }}>Beta</span>
                              )}
                              </div>
                            );
                          }
                          return (
                            <Link
                              key={child.id}
                              to={child.path}
                              className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                              style={{
                                backgroundColor: childActive ? B.olive : 'transparent',
                                color: childActive ? '#fff' : B.sand,
                                fontFamily: 'Gill Sans, sans-serif',
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: childActive ? 'white' : B.stone + '80' }} />
                              <span>{child.label}</span>
                              {child.beta && (
                                <span className="text-[8px] font-bold px-1 py-px rounded uppercase tracking-wider" style={{ backgroundColor: '#B87333', color: '#fff' }}>Beta</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif'
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                </Link>
              );
            })}

          </div>
        )}


        {/* The Barn - Collapsible */}
        <button
          onClick={() => toggleSection('barn')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnBarnPage ? B.olive : 'transparent',
            color: isOnBarnPage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Warehouse className="w-4 h-4" />
            <span>{t('nav.theBarn')}</span>
          </div>
          {expanded.barn ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.barn && (
          <div className="ml-3">
            {barnItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const canAccess = hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif'
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                </Link>
              );
            })}
          </div>
        )}

        {/* Trading Post - Collapsible */}
        <button
          onClick={() => toggleSection('tradingPost')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnTradingPostPage ? B.olive : 'transparent',
            color: isOnTradingPostPage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            <span>{t('nav.tradingPost')}</span>
          </div>
          {expanded.tradingPost ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.tradingPost && (
          <div className="ml-3">
            {tradingPostItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const canAccess = hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                    {item.underConstruction && (
                      <Construction className="w-3 h-3 ml-auto" style={{ color: B.stone }} />
                    )}
                  </div>
                );
              }

              /* QBR with nested quarter → section menu */
              if (item.children) {
                const isQbrPage = location.pathname.startsWith('/supply-chain/qbr');
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => toggleSection('qbr')}
                      className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: isQbrPage && !expanded.qbr ? B.olive : 'transparent',
                        color: isQbrPage ? '#fff' : B.sand,
                        fontFamily: 'Gill Sans, sans-serif',
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                      {expanded.qbr ? <ChevronDown className="w-3 h-3 ml-auto" /> : <ChevronRight className="w-3 h-3 ml-auto" />}
                    </button>
                    {expanded.qbr && item.children.map((qtr) => (
                      <div key={qtr.id} className="ml-3">
                        <button
                          onClick={() => toggleSection(qtr.id)}
                          className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                          style={{
                            color: B.sand,
                            fontFamily: 'Gill Sans, sans-serif',
                            fontSize: '11px',
                          }}
                        >
                          {expanded[qtr.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          <span>{qtr.label}</span>
                        </button>
                        {expanded[qtr.id] && qtr.children && (
                          <div className="ml-3">
                            {qtr.children.map((sec) => {
                              // QBR sub-items with their own route (e.g. the
                              // Wholesale page) link via sec.path instead of a
                              // hash section of the QBR file. The Construction
                              // icon appears only while sec.underConstruction
                              // is set (v1.36.0 placeholder pattern) — routing
                              // must NOT depend on that flag, or removing it
                              // silently turns the link into basePath#undefined.
                              if (sec.path) {
                                const secActive = location.pathname === sec.path;
                                return (
                                  <Link
                                    key={sec.id}
                                    to={sec.path}
                                    title={sec.tooltip || sec.label}
                                    className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                                    style={{
                                      backgroundColor: secActive ? B.olive : 'transparent',
                                      color: secActive ? '#fff' : B.sand,
                                      fontFamily: 'Gill Sans, sans-serif',
                                      fontSize: '11px',
                                    }}
                                  >
                                    <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: secActive ? B.rust : 'rgba(245,240,232,0.3)', flexShrink: 0 }} />
                                    <span>{sec.label}</span>
                                    {sec.underConstruction && <Construction className="w-3 h-3 ml-auto" style={{ color: secActive ? B.cream : B.rust }} />}
                                  </Link>
                                );
                              }
                              const secActive = location.pathname === qtr.basePath && location.hash === `#${sec.hash}`;
                              return (
                                <Link
                                  key={sec.id}
                                  to={`${qtr.basePath}#${sec.hash}`}
                                  className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                                  style={{
                                    backgroundColor: secActive ? B.olive : 'transparent',
                                    color: secActive ? '#fff' : B.sand,
                                    fontFamily: 'Gill Sans, sans-serif',
                                    fontSize: '11px',
                                  }}
                                >
                                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: secActive ? B.rust : 'rgba(245,240,232,0.3)', flexShrink: 0 }} />
                                  <span>{sec.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif',
                    opacity: item.underConstruction ? 0.6 : 1
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  {item.underConstruction && (
                    <Construction className="w-3 h-3 ml-auto" style={{ color: B.rust }} />
                  )}
                </Link>
              );
            })}

            {/* Vendors - Nested Submenu */}
            <button
              onClick={() => toggleSection('vendors')}
              className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
              style={{
                backgroundColor: isOnVendorsPage ? B.olive : 'transparent',
                color: isOnVendorsPage ? '#fff' : B.sand,
                fontFamily: 'Gill Sans, sans-serif'
              }}
            >
              <div className="flex items-center gap-2">
                <Factory className="w-3.5 h-3.5" />
                <span>Vendors</span>
              </div>
              {expanded.vendors ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>

            {expanded.vendors && (
              <div className="ml-3">
                {vendorsItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const canAccess = hasAccess('page', item.id);

                  if (!canAccess) {
                    return (
                      <div
                        key={item.id}
                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                        style={{
                          backgroundColor: 'transparent',
                          color: B.stone,
                          fontFamily: 'Gill Sans, sans-serif',
                          opacity: 0.5
                        }}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                        {item.underConstruction && (
                          <Construction className="w-3 h-3 ml-auto" style={{ color: B.stone }} />
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: active ? B.olive : 'transparent',
                        color: active ? '#fff' : B.sand,
                        fontFamily: 'Gill Sans, sans-serif',
                        opacity: item.underConstruction ? 0.6 : 1
                      }}
                    >
                      <Icon className="w-3 h-3" />
                      <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                      {item.underConstruction && (
                        <Construction className="w-3 h-3 ml-auto" style={{ color: B.rust }} />
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* The Mercantile - Collapsible (WFX-sourced merch data) */}
        <button
          onClick={() => toggleSection('mercantile')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnMercantilePage ? B.olive : 'transparent',
            color: isOnMercantilePage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            <span>The Workshop</span>
          </div>
          {expanded.mercantile ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.mercantile && (
          <div className="ml-3">
            {mercantileItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const canAccess = hasAccess('page', item.id);
              const hasChildren = item.children && item.children.length > 0;
              const subKey = `${item.id.replace(/-/g, '')}Sub`; // e.g. 'plmDashboardsSub'
              const subOpen = expanded[subKey];

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                    {item.underConstruction && (
                      <Construction className="w-3 h-3 ml-auto" style={{ color: B.stone }} />
                    )}
                  </div>
                );
              }

              if (hasChildren) {
                return (
                  <div key={item.id}>
                    {/* Parent toggle — expands child links */}
                    <button
                      onClick={() => toggleSection(subKey)}
                      className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: active ? B.olive + 'cc' : 'transparent',
                        color: active ? '#fff' : B.sand,
                        fontFamily: 'Gill Sans, sans-serif',
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                      </div>
                      {subOpen
                        ? <ChevronDown className="w-3 h-3 opacity-60" />
                        : <ChevronRight className="w-3 h-3 opacity-60" />}
                    </button>
                    {/* Child links */}
                    {subOpen && (
                      <div className="ml-4 mb-1">
                        {/* One child's path can be a prefix of another's
                            (Material Planning: Leather owns /material-planning,
                            Apparel /material-planning/apparel, Accessories
                            /material-planning/accessories), so an exact
                            match always wins — otherwise Leather would light up
                            while you're on Apparel. */}
                        {item.children.map((child) => {
                          const exactChild = item.children.find((c) => c.path === location.pathname);
                          const childActive = exactChild
                            ? exactChild.id === child.id
                            : location.pathname.startsWith(child.path + '/');
                          return (
                            <Link
                              key={child.id}
                              to={child.path}
                              className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                              style={{
                                backgroundColor: childActive ? B.olive : 'transparent',
                                color: childActive ? '#fff' : B.sand,
                                fontFamily: 'Gill Sans, sans-serif',
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: childActive ? 'white' : B.stone + '80' }} />
                              <span>{child.label}</span>
                              {child.beta && (
                                <span className="text-[8px] font-bold px-1 py-px rounded uppercase tracking-wider" style={{ backgroundColor: '#B87333', color: '#fff' }}>Beta</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif',
                    opacity: item.underConstruction ? 0.6 : 1
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  {item.underConstruction && (
                    <Construction className="w-3 h-3 ml-auto" style={{ color: B.rust }} />
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Howdy, Partner - Collapsible */}
        <button
          onClick={() => toggleSection('howdyPartner')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnHowdyPartnerPage ? B.olive : 'transparent',
            color: isOnHowdyPartnerPage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>{t('nav.howdyPartner')}</span>
          </div>
          {expanded.howdyPartner ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.howdyPartner && (
          <div className="ml-3">
            {howdyPartnerItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const canAccess = hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                    {item.underConstruction && (
                      <Construction className="w-3 h-3 ml-auto" style={{ color: B.stone }} />
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif',
                    opacity: item.underConstruction ? 0.6 : 1
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  {item.underConstruction && (
                    <Construction className="w-3 h-3 ml-auto" style={{ color: B.rust }} />
                  )}
                </Link>
              );
            })}

          </div>
        )}

        {/* The Exchange - Collapsible */}
        <button
          onClick={() => toggleSection('exchange')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnExchangePage ? B.olive : 'transparent',
            color: isOnExchangePage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            <span>{t('nav.theExchange')}</span>
          </div>
          {expanded.exchange ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.exchange && (
          <div className="ml-3">
            {exchangeItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const canAccess = hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                    {item.underConstruction && (
                      <Construction className="w-3 h-3 ml-auto" style={{ color: B.stone }} />
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif',
                    opacity: item.underConstruction ? 0.6 : 1
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  {item.underConstruction && (
                    <Construction className="w-3 h-3 ml-auto" style={{ color: B.rust }} />
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Wholesale - Collapsible */}
        <button
          onClick={() => toggleSection('wholesale')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnWholesalePage ? B.olive : 'transparent',
            color: isOnWholesalePage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            <span>Wholesale</span>
          </div>
          {expanded.wholesale ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {expanded.wholesale && (
          <div className="ml-3">
            {wholesaleItems.map((item) => {
              const Icon = item.icon;
              // Exact match for the /wholesale landing so it isn't highlighted on sub-pages
              const active = item.path === '/wholesale' ? location.pathname === '/wholesale' : isActive(item.path);
              const canAccess = hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif'
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="mb-4">
        <div className="h-px mb-3" style={{ backgroundColor: B.olive }} />

        {/* Sheriff's Office (Admin) - Collapsible */}
        <button
          onClick={() => toggleSection('sheriff')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isOnSheriffPage ? B.olive : 'transparent',
            color: isOnSheriffPage ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>{t('nav.sheriffsOffice')}</span>
          </div>
          {expanded.sheriff ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Sheriff's Office Items */}
        {expanded.sheriff && (
          <div className="ml-3">
            {sheriffItems.map((item) => {
              const Icon = item.icon;
              const active =
                (item.path && isActive(item.path))
                || (item.id === 'admin-carriers' && isOnCarrierSubPage)
                || (item.id === 'data-corral'    && isOnDataCorralSubPage)
                || (item.id === 'haystack'       && isOnHaystackSubPage)
                || (item.id === 'ranchhands'     && isOnRanchhandsSubPage);
              const canAccess = item.adminOnly ? isAdmin : hasAccess('page', item.id);

              if (!canAccess) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent',
                      color: B.stone,
                      fontFamily: 'Gill Sans, sans-serif',
                      opacity: 0.5
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                  </div>
                );
              }

              // Reusable renderer for collapsible parents + their sub-items.
              // parentLink=true gives the parent a clickable link to its own page
              // (Data Corral); parentLink=false treats the parent as toggle-only
              // (The Haystack, Ranchhands).
              const renderGroup = ({ subItems, expandedKey, parentLink, parentSelfLabel }) => (
                <div key={item.id}>
                  <button
                    onClick={() => toggleSection(expandedKey)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                    style={{
                      backgroundColor: active ? B.olive : 'transparent',
                      color: active ? '#fff' : B.sand,
                      fontFamily: 'Gill Sans, sans-serif'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5" />
                      <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                    </div>
                    {expanded[expandedKey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {expanded[expandedKey] && (
                    <div className="ml-3">
                      {parentLink && item.path && (
                        <Link
                          to={item.path}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                          style={{
                            backgroundColor: isActive(item.path) ? B.olive : 'transparent',
                            color: isActive(item.path) ? '#fff' : B.sand,
                            fontFamily: 'Gill Sans, sans-serif'
                          }}
                        >
                          <Icon className="w-3 h-3" />
                          <span>{parentSelfLabel || item.label}</span>
                        </Link>
                      )}
                      {subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const subActive = isActive(subItem.path);
                        const subCanAccess = subItem.adminOnly ? isAdmin : hasAccess('page', subItem.id);
                        if (!subCanAccess) {
                          return (
                            <div key={subItem.id}
                              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                              style={{ backgroundColor: 'transparent', color: B.stone, fontFamily: 'Gill Sans, sans-serif', opacity: 0.5 }}>
                              <SubIcon className="w-3 h-3" />
                              <span>{subItem.label}</span>
                            </div>
                          );
                        }
                        return (
                          <Link key={subItem.id} to={subItem.path}
                            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                            style={{
                              backgroundColor: subActive ? B.olive : 'transparent',
                              color: subActive ? '#fff' : B.sand,
                              fontFamily: 'Gill Sans, sans-serif'
                            }}>
                            <SubIcon className="w-3 h-3" />
                            <span>{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );

              if (item.id === 'data-corral') return renderGroup({ subItems: dataCorralSubItems, expandedKey: 'dataCorralSub', parentLink: true, parentSelfLabel: 'Data Syncs' });
              if (item.id === 'haystack')    return renderGroup({ subItems: haystackSubItems,    expandedKey: 'haystack',      parentLink: false });
              if (item.id === 'ranchhands')  return renderGroup({ subItems: ranchhandsSubItems,  expandedKey: 'ranchhands',    parentLink: false });

              // Special handling for Carrier Management - has submenu
              if (item.id === 'admin-carriers') {
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => toggleSection('carrierSub')}
                      className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: active ? B.olive : 'transparent',
                        color: active ? '#fff' : B.sand,
                        fontFamily: 'Gill Sans, sans-serif'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                      </div>
                      {expanded.carrierSub ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </button>
                    {expanded.carrierSub && (
                      <div className="ml-3">
                        {/* Carrier Setup link */}
                        <Link
                          to={item.path}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                          style={{
                            backgroundColor: isActive(item.path) ? B.olive : 'transparent',
                            color: isActive(item.path) ? '#fff' : B.sand,
                            fontFamily: 'Gill Sans, sans-serif'
                          }}
                        >
                          <Icon className="w-3 h-3" />
                          <span>Carrier Setup</span>
                        </Link>
                        {/* Carrier sub-items */}
                        {carrierSubItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subActive = isActive(subItem.path);
                          const subCanAccess = hasAccess('page', subItem.id);

                          if (!subCanAccess) {
                            return (
                              <div
                                key={subItem.id}
                                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
                                style={{
                                  backgroundColor: 'transparent',
                                  color: B.stone,
                                  fontFamily: 'Gill Sans, sans-serif',
                                  opacity: 0.5
                                }}
                              >
                                <SubIcon className="w-3 h-3" />
                                <span>{subItem.label}</span>
                              </div>
                            );
                          }

                          return (
                            <Link
                              key={subItem.id}
                              to={subItem.path}
                              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                              style={{
                                backgroundColor: subActive ? B.olive : 'transparent',
                                color: subActive ? '#fff' : B.sand,
                                fontFamily: 'Gill Sans, sans-serif'
                              }}
                            >
                              <SubIcon className="w-3 h-3" />
                              <span>{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: active ? B.olive : 'transparent',
                    color: active ? '#fff' : B.sand,
                    fontFamily: 'Gill Sans, sans-serif'
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>{item.beta && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}>BETA</span>}
                </Link>
              );
            })}
          </div>
        )}
        
        {/* Divider */}
        <div className="h-px my-3" style={{ backgroundColor: B.olive }} />
        
        {/* User Guide - Always accessible */}
        <Link
          to="/user-guide"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{
            backgroundColor: isActive('/user-guide') ? B.olive : 'transparent',
            color: isActive('/user-guide') ? '#fff' : B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <Book className="w-4 h-4" />
          <span>User Guide</span>
        </Link>

        {/* Feature Request / Your Ideas */}
        {hasAccess('page', 'feature-request') ? (
          <Link
            to="/feature-request"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
            style={{
              backgroundColor: isActive('/feature-request') ? B.olive : 'transparent',
              color: isActive('/feature-request') ? '#fff' : B.sand,
              fontFamily: 'Gill Sans, sans-serif'
            }}
          >
            <Lightbulb className="w-4 h-4" />
            <span>Your Ideas</span>
          </Link>
        ) : (
          <div
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 cursor-not-allowed"
            style={{
              backgroundColor: 'transparent',
              color: B.stone,
              fontFamily: 'Gill Sans, sans-serif',
              opacity: 0.5
            }}
          >
            <Lightbulb className="w-4 h-4" />
            <span>Your Ideas</span>
          </div>
        )}
      </div>
      
      {/* Language Switcher */}
      <div className="mb-3">
        <button
          onClick={toggleLanguage}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
          style={{
            backgroundColor: 'transparent',
            color: B.sand,
            fontFamily: 'Gill Sans, sans-serif'
          }}
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>{t('language.selectLanguage')}</span>
          </div>
          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: B.olive }}>
            {currentLang === 'en' ? 'EN' : 'ES'}
          </span>
        </button>
      </div>

      {/* User info and logout */}
      <div className="border-t pt-4" style={{ borderColor: B.olive }}>
        <p className="text-xs truncate mb-2" style={{ color: B.sand }}>{user?.email}</p>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs w-full hover:opacity-80 transition"
          style={{ backgroundColor: B.rust, color: '#fff' }}
        >
          <LogOut className="w-4 h-4" /> {t('nav.logout')}
        </button>
      </div>
    </div>
  );
}

export default memo(Sidebar);
